import { createFileRoute } from '@tanstack/react-router';
import { correoEntregable } from '@taller/shared';
import {
  Ellipsis,
  KeyRound,
  LockKeyhole,
  Mail,
  Plus,
  Power,
  Search,
  UserCog,
  Users,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  DialogoEditarUsuario,
  DialogoNuevoUsuario,
  DialogoPin,
  DialogoRestablecer,
} from '@/admin/DialogosUsuario';
import { useUsuarios } from '@/datos/consultas';
import { callables, mensajeError } from '@/datos/acciones';
import type { UsuarioDoc } from '@/datos/tipos';
import { exigir } from '@/lib/guardias';
import { nombreRol, permisos, useSesionActiva } from '@/lib/sesion';
import { Encabezado } from '@/shell/Encabezado';
import { Avatar } from '@/shell/Shell';
import { ErrorCarga, Esqueleto, Insignia, Segmentado, Tarjeta, Vacio } from '@/ui/basicos';
import { Boton } from '@/ui/boton';
import { cn } from '@/ui/cn';
import { Confirmar } from '@/ui/confirmar';
import { Menu, MenuContenido, MenuDisparador, MenuItem, MenuSeparador } from '@/ui/menu';
import { Contenedor } from '@/ui/pagina';

export const Route = createFileRoute('/_app/admin/usuarios')({
  beforeLoad: exigir(permisos.administrar),
  component: Pagina,
});

type Filtro = 'activos' | 'inactivos' | 'todos';

type Dialogo =
  | { tipo: 'nuevo' }
  | { tipo: 'rol'; u: UsuarioDoc }
  | { tipo: 'pin'; u: UsuarioDoc }
  | { tipo: 'clave'; u: UsuarioDoc }
  | { tipo: 'estado'; u: UsuarioDoc }
  | { tipo: 'bienvenida'; u: UsuarioDoc };

/** Welcome e-mail again: only while the person still has the temporary password. */
const puedeReenviarBienvenida = (u: UsuarioDoc, esYo: boolean) =>
  !esYo && u.debeCambiarPassword === true && correoEntregable(u.email);

async function reenviarBienvenida(u: UsuarioDoc) {
  try {
    await callables.reenviarBienvenida({ uid: u.id });
    toast.success(`Le enviamos otro correo de bienvenida a ${u.nombre}`, {
      description: 'El enlace vence en 1 hora. Su contraseña temporal sigue sirviendo.',
    });
  } catch (e) {
    toast.error('No se pudo reenviar la bienvenida', { description: mensajeError(e) });
    throw e;
  }
}

function Pagina() {
  const s = useSesionActiva();
  const q = useUsuarios();
  const [filtro, setFiltro] = useState<Filtro>('activos');
  const [texto, setTexto] = useState('');
  // The last dialog stays mounted after closing so its exit animation plays;
  // `n` changes on every open so forms always start clean.
  const [ultimo, setUltimo] = useState<Dialogo | null>(null);
  const [abierto, setAbierto] = useState(false);
  const [n, setN] = useState(0);
  const abrir = (d: Dialogo) => {
    setN((x) => x + 1);
    setUltimo(d);
    setAbierto(true);
  };
  const cerrar = (v: boolean) => !v && setAbierto(false);
  const dialogo = abierto ? ultimo : null;

  const todos = q.data ?? [];
  const activos = todos.filter((u) => u.activo).length;
  const filas = useMemo(() => {
    const t = texto.trim().toLowerCase();
    return (q.data ?? [])
      .filter((u) => (filtro === 'todos' ? true : filtro === 'activos' ? u.activo : !u.activo))
      .filter(
        (u) =>
          !t || u.nombre.toLowerCase().includes(t) || (u.email ?? '').toLowerCase().includes(t),
      )
      .sort(
        (a, b) => Number(b.activo) - Number(a.activo) || a.nombre.localeCompare(b.nombre, 'es'),
      );
  }, [q.data, filtro, texto]);

  const cambiarEstado = async (u: UsuarioDoc) => {
    try {
      await callables.actualizarUsuario({ uid: u.id, activo: !u.activo });
      toast.success(
        u.activo ? `${u.nombre} ya no puede ingresar` : `${u.nombre} puede volver a ingresar`,
        {
          description: u.activo
            ? 'Su correo y su PIN quedaron bloqueados y sus sesiones abiertas se cerraron. Su historial se conserva.'
            : undefined,
        },
      );
    } catch (e) {
      toast.error('No se pudo cambiar el acceso', { description: mensajeError(e) });
      throw e;
    }
  };

  const nuevo = (
    <Boton
      variante="primario"
      onClick={() => abrir({ tipo: 'nuevo' })}
      aria-label="Nueva persona"
      className="max-sm:size-11 max-sm:px-0"
    >
      <Plus /> <span className="max-sm:hidden">Nueva persona</span>
    </Boton>
  );

  return (
    <>
      <Encabezado
        titulo="Usuarios"
        ayuda="usuarios"
        subtitulo={
          q.data
            ? `${activos} ${activos === 1 ? 'persona activa' : 'personas activas'}`
            : 'Cargando…'
        }
        acciones={nuevo}
        buscador={false}
      />
      <Contenedor>
        <div className="flex flex-wrap items-center gap-2.5">
          <Segmentado
            etiqueta="Mostrar"
            valor={filtro}
            onCambio={setFiltro}
            opciones={[
              { valor: 'activos', texto: 'Activas', cantidad: activos },
              { valor: 'inactivos', texto: 'Desactivadas', cantidad: todos.length - activos },
              { valor: 'todos', texto: 'Todas' },
            ]}
          />
          <label className="relative max-sm:w-full">
            <span className="sr-only">Buscar persona</span>
            <Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-text-2" />
            <input
              value={texto}
              onChange={(e) => setTexto(e.target.value)}
              placeholder="Nombre o email"
              className="h-[38px] w-full rounded-[10px] border border-border-input bg-surface pr-3 pl-9 text-[14px] focus:border-primary focus:shadow-[0_0_0_1px_var(--tm-primary),0_0_0_4px_var(--tm-primary-soft)] focus:outline-none sm:w-[260px]"
            />
          </label>
        </div>

        {q.isError ? (
          <ErrorCarga error={q.error} />
        ) : !q.data ? (
          <Tarjeta className="flex flex-col gap-3 p-5" aria-busy="true">
            {Array.from({ length: 5 }, (_, i) => (
              <Esqueleto key={i} className="h-12" />
            ))}
          </Tarjeta>
        ) : filas.length === 0 ? (
          <Tarjeta>
            <Vacio
              icono={<Users />}
              titulo={texto ? 'Nadie coincide con la búsqueda' : 'No hay personas en esta lista'}
              accion={!texto && filtro === 'activos' ? nuevo : undefined}
            >
              {texto
                ? 'Prueba con otra parte del nombre o el email.'
                : 'Crea a las personas del taller: cada una entra con su email.'}
            </Vacio>
          </Tarjeta>
        ) : (
          <Tarjeta className="overflow-hidden">
            <table className="w-full border-collapse text-[15px]">
              <caption className="sr-only">Personas del taller</caption>
              <thead className="max-md:sr-only">
                <tr className="bg-surface-muted text-left text-[12.5px] tracking-[0.06em] text-text-2 uppercase">
                  <th scope="col" className="px-4 py-3 font-bold sm:px-5">
                    Persona
                  </th>
                  <th scope="col" className="px-4 py-3 font-bold max-lg:hidden">
                    Email
                  </th>
                  <th scope="col" className="px-4 py-3 font-bold max-md:hidden">
                    Rol
                  </th>
                  <th scope="col" className="px-4 py-3 font-bold max-md:hidden">
                    Equipos compartidos
                  </th>
                  <th scope="col" className="px-4 py-3 font-bold max-md:hidden">
                    Estado
                  </th>
                  <th scope="col" className="w-16 px-4 py-3">
                    <span className="sr-only">Acciones</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {filas.map((u) => (
                  <Fila key={u.id} u={u} esYo={u.id === s.uid} onAccion={abrir} />
                ))}
              </tbody>
            </table>
          </Tarjeta>
        )}

        <p className="text-[13px] leading-relaxed text-text-2">
          Cada persona entra con su email y su contraseña, en la app y en el backoffice. El PIN solo
          sirve en las tablets compartidas del taller; se guarda cifrado y nadie puede verlo. Tras 5
          intentos fallidos queda bloqueado unos minutos: asignar un PIN nuevo lo desbloquea.
        </p>
      </Contenedor>

      <DialogoNuevoUsuario
        key={`nuevo-${ultimo?.tipo === 'nuevo' ? n : 0}`}
        abierto={dialogo?.tipo === 'nuevo'}
        onCambio={cerrar}
      />
      {ultimo?.tipo === 'rol' && (
        <DialogoEditarUsuario
          key={`rol-${n}`}
          usuario={ultimo.u}
          esYo={ultimo.u.id === s.uid}
          abierto={dialogo?.tipo === 'rol'}
          onCambio={cerrar}
        />
      )}
      {ultimo?.tipo === 'pin' && (
        <DialogoPin
          key={`pin-${n}`}
          usuario={ultimo.u}
          abierto={dialogo?.tipo === 'pin'}
          onCambio={cerrar}
        />
      )}
      {ultimo?.tipo === 'clave' && (
        <DialogoRestablecer
          key={`clave-${n}`}
          uid={ultimo.u.id}
          nombre={ultimo.u.nombre}
          abierto={dialogo?.tipo === 'clave'}
          onCambio={cerrar}
        />
      )}
      {ultimo?.tipo === 'bienvenida' && (
        <Confirmar
          abierto={dialogo?.tipo === 'bienvenida'}
          onCambio={cerrar}
          titulo={`¿Reenviar la bienvenida a ${ultimo.u.nombre}?`}
          textoConfirmar="Reenviar correo"
          onConfirmar={() => reenviarBienvenida(ultimo.u)}
        >
          Le llega a {ultimo.u.email} un enlace nuevo para elegir su contraseña (vence en 1 hora).
          Su contraseña temporal no cambia. Puedes reenviarla hasta 3 veces al día.
        </Confirmar>
      )}
      {ultimo?.tipo === 'estado' && (
        <Confirmar
          abierto={dialogo?.tipo === 'estado'}
          onCambio={cerrar}
          titulo={
            ultimo.u.activo
              ? `¿Desactivar a ${ultimo.u.nombre}?`
              : `¿Reactivar a ${ultimo.u.nombre}?`
          }
          textoConfirmar={ultimo.u.activo ? 'Desactivar' : 'Reactivar'}
          peligro={ultimo.u.activo}
          onConfirmar={() => cambiarEstado(ultimo.u)}
        >
          {ultimo.u.activo
            ? 'No podrá entrar con su email ni con su PIN, y sus sesiones abiertas se cierran. Las inspecciones que registró se conservan con su nombre.'
            : 'Vuelve a entrar con su email y, si tiene PIN, en las tablets compartidas.'}
        </Confirmar>
      )}
    </>
  );
}

function Fila({
  u,
  esYo,
  onAccion,
}: {
  u: UsuarioDoc;
  esYo: boolean;
  onAccion: (d: Dialogo) => void;
}) {
  const rol = nombreRol[u.rol];
  return (
    <tr
      className={cn(
        'border-t border-border align-middle first:border-t-0 md:first:border-t',
        // Inactive: muted colors, not opacity (text would drop below AA).
        !u.activo && 'bg-surface-muted/40 text-text-2',
      )}
    >
      <td className="px-4 py-3 sm:px-5">
        <div className="flex items-center gap-3">
          <Avatar
            nombre={u.nombre}
            className={cn(
              'size-9 bg-surface-muted text-text-long ring-1 ring-border',
              !u.activo && 'opacity-50',
            )}
          />
          <div className="min-w-0">
            <p className="flex items-center gap-2 font-bold">
              <span className="truncate">{u.nombre}</span>
              {esYo && <span className="text-[12.5px] font-normal text-text-2">(tú)</span>}
            </p>
            <p className="truncate text-[13px] text-text-2 lg:hidden">{u.email ?? 'Sin email'}</p>
            {u.debeCambiarPassword && u.activo && (
              <p className="text-[12.5px] text-text-2">Aún usa su contraseña temporal</p>
            )}
            {/* Below md: role, PIN and state under the name. */}
            <div className="mt-1.5 flex flex-wrap gap-1.5 md:hidden">
              <Insignia>{rol}</Insignia>
              <AccesoPin u={u} />
              {!u.activo && <Insignia punto>Desactivada</Insignia>}
            </div>
          </div>
        </div>
      </td>
      <td className="px-4 py-3 text-text-long max-lg:hidden">
        <span className="block max-w-[260px] truncate">{u.email ?? 'Sin email'}</span>
      </td>
      <td className="px-4 py-3 max-md:hidden">{rol}</td>
      <td className="px-4 py-3 max-md:hidden">
        <AccesoPin u={u} />
      </td>
      <td className="px-4 py-3 max-md:hidden">
        {u.activo ? <Insignia tono="exito">Activa</Insignia> : <Insignia>Desactivada</Insignia>}
      </td>
      <td className="px-4 py-3 text-right">
        <Menu>
          <MenuDisparador asChild>
            <Boton tamano="iconoSm" aria-label={`Acciones de ${u.nombre}`}>
              <Ellipsis />
            </Boton>
          </MenuDisparador>
          <MenuContenido className="w-60">
            {!esYo && (
              <MenuItem
                icono={<LockKeyhole />}
                onSelect={() => onAccion({ tipo: 'clave', u })}
                disabled={!u.activo}
              >
                Restablecer contraseña
              </MenuItem>
            )}
            {puedeReenviarBienvenida(u, esYo) && (
              <MenuItem
                icono={<Mail />}
                onSelect={() => onAccion({ tipo: 'bienvenida', u })}
                disabled={!u.activo}
              >
                Reenviar bienvenida
              </MenuItem>
            )}
            <MenuItem
              icono={<KeyRound />}
              onSelect={() => onAccion({ tipo: 'pin', u })}
              disabled={!u.activo}
            >
              {u.tienePin ? 'Cambiar o quitar PIN' : 'Asignar PIN'}
            </MenuItem>
            <MenuItem icono={<UserCog />} onSelect={() => onAccion({ tipo: 'rol', u })}>
              Cambiar rol
            </MenuItem>
            {!esYo && (
              <>
                <MenuSeparador />
                <MenuItem
                  icono={<Power />}
                  peligro={u.activo}
                  onSelect={() => onAccion({ tipo: 'estado', u })}
                >
                  {u.activo ? 'Desactivar' : 'Reactivar'}
                </MenuItem>
              </>
            )}
          </MenuContenido>
        </Menu>
      </td>
    </tr>
  );
}

/** Whether the person can sign in on shared tablets (has a PIN). */
function AccesoPin({ u }: { u: UsuarioDoc }) {
  return u.tienePin ? (
    <Insignia tono="exito" className="gap-1">
      <KeyRound className="size-3.5" aria-hidden="true" />
      PIN
    </Insignia>
  ) : (
    <span className="text-[14px] text-text-2">Sin PIN</span>
  );
}
